import { useEffect, useRef, type ReactNode } from 'react';
import { describeError } from '../api.ts';

export const STATE_LABELS: Record<string, string> = {
  pending_review: 'In review',
  approved: 'Approved',
  rejected: 'Rejected',
  paused: 'Paused',
  archived: 'Archived',
};

export function StateBadge({ state, live }: { state: string | null; live?: boolean }) {
  if (state === null) return <span className="badge badge-none">Not offered</span>;
  const label = state === 'approved' && live === false ? 'Approved · not live' : (STATE_LABELS[state] ?? state);
  const tone = state === 'approved' ? (live === false ? 'warn' : 'ok') : state === 'pending_review' ? 'review' : 'muted';
  return <span className={`badge badge-${tone}`}>{label}</span>;
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      {label}…
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="error-box" role="alert">
      <strong>Couldn’t load this.</strong> {describeError(error)}
      {onRetry ? (
        <button type="button" className="btn btn-small" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

/** Side panel for details; Escape or the backdrop closes it, focus moves into it on open and back on close. */
export function Drawer({ title, onClose, children, wide = false, footer }: { title: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean; footer?: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="drawer-layer">
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <div className={`drawer ${wide ? 'drawer-wide' : ''}`} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Details'} tabIndex={-1} ref={panel}>
        <header className="drawer-head">
          <h2>{title}</h2>
          <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="Close">
            Esc
          </button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer ? <footer className="drawer-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

export function Modal({ title, onClose, children, actions }: { title: string; onClose: () => void; children: ReactNode; actions: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    box.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-layer">
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={box}>
        <h2>{title}</h2>
        <div className="modal-body">{children}</div>
        <div className="modal-actions">{actions}</div>
      </div>
    </div>
  );
}

const DAY = 24 * 60 * 60 * 1000;

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "in 3 days", "2 days ago", "today". */
export function relativeDays(iso: string | null, now = Date.now()): string {
  if (!iso) return 'no end date';
  const days = Math.round((new Date(iso).getTime() - now) / DAY);
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
  return `${-days} day${days === -1 ? '' : 's'} ago`;
}

export function age(iso: string, now = Date.now()): string {
  const minutes = Math.round((now - new Date(iso).getTime()) / 60000);
  if (minutes < 60) return `${Math.max(1, minutes)} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}

export function Swatches({ colors }: { colors: Record<string, string> }) {
  return (
    <span className="swatches" aria-label="Theme colours">
      {Object.entries(colors).map(([name, value]) => (
        <span key={name} className="swatch" style={{ background: value }} title={`${name} ${value}`} />
      ))}
    </span>
  );
}
